import * as Cause from "effect/Cause";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Layer from "effect/Layer";
import * as ManagedRuntime from "effect/ManagedRuntime";
import * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";
import { isTransportConnectionErrorMessage } from "./transportError.js";
import { createWsRpcProtocolLayer, makeWsRpcProtocolClient, } from "./wsRpcProtocol.js";
const DEFAULT_SUBSCRIPTION_RETRY_DELAY = Duration.millis(250);
const NOOP = () => undefined;
function formatErrorMessage(error) {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return String(error);
}
export class WsTransport {
    url;
    lifecycleHandlers;
    options;
    disposed = false;
    hasReportedTransportDisconnect = false;
    intentionalCloseDepth = 0;
    nextSessionId = 0;
    activeSessionId = 0;
    lastHeartbeatPongAt = null;
    streamRequestStartListeners = new Set();
    reconnectChain = Promise.resolve();
    session;
    constructor(url, lifecycleHandlers, options) {
        this.url = url;
        this.lifecycleHandlers = lifecycleHandlers;
        this.options = options;
        this.session = this.createSession();
    }
    async request(execute) {
        if (this.disposed) {
            throw new Error("Transport disposed");
        }
        const session = this.session;
        const client = await session.clientPromise;
        return await session.runtime.runPromise(Effect.suspend(() => execute(client)));
    }
    async requestStream(connect, listener) {
        if (this.disposed) {
            throw new Error("Transport disposed");
        }
        const session = this.session;
        const client = await session.clientPromise;
        await session.runtime.runPromise(Stream.runForEach(connect(client), (value) => Effect.sync(() => {
            try {
                listener(value);
            }
            catch {
                // Ignore listener errors so the stream can finish cleanly.
            }
        })));
    }
    subscribe(connect, listener, options) {
        if (this.disposed) {
            return NOOP;
        }
        let active = true;
        let hasReceivedValue = false;
        const retryDelayMs = Duration.toMillis(Duration.fromInputUnsafe(options?.retryDelay ?? DEFAULT_SUBSCRIPTION_RETRY_DELAY));
        let cancelCurrentStream = NOOP;
        const onStreamRequestStart = (info) => {
            if (!hasReceivedValue ||
                !active ||
                (options?.tag !== undefined && info.tag !== options.tag)) {
                return;
            }
            try {
                options?.onResubscribe?.();
            }
            catch {
                // Ignore reconnect hook failures so the stream can recover.
            }
        };
        this.streamRequestStartListeners.add(onStreamRequestStart);
        void (async () => {
            for (;;) {
                if (!active || this.disposed) {
                    return;
                }
                const session = this.session;
                try {
                    if (hasReceivedValue) {
                        try {
                            options?.onResubscribe?.();
                        }
                        catch {
                            // Ignore reconnect hook failures so the stream can recover.
                        }
                        if (!active || this.disposed) {
                            return;
                        }
                    }
                    const runningStream = this.runStreamOnSession(session, connect, listener, () => active, () => {
                        this.hasReportedTransportDisconnect = false;
                        hasReceivedValue = true;
                    });
                    cancelCurrentStream = runningStream.cancel;
                    await runningStream.completed;
                    cancelCurrentStream = NOOP;
                }
                catch (error) {
                    cancelCurrentStream = NOOP;
                    if (!active || this.disposed) {
                        return;
                    }
                    // Skip retry if the session has already been replaced by a reconnect.
                    if (session !== this.session) {
                        continue;
                    }
                    const formattedError = formatErrorMessage(error);
                    if (!isTransportConnectionErrorMessage(formattedError)) {
                        this.logWarning("WebSocket RPC subscription failed", { error: formattedError });
                        return;
                    }
                    if (!this.hasReportedTransportDisconnect) {
                        this.logWarning("WebSocket RPC subscription disconnected", {
                            error: formattedError,
                        });
                    }
                    this.hasReportedTransportDisconnect = true;
                    await sleep(retryDelayMs);
                }
            }
        })();
        return () => {
            active = false;
            this.streamRequestStartListeners.delete(onStreamRequestStart);
            cancelCurrentStream();
        };
    }
    async reconnect() {
        if (this.disposed) {
            throw new Error("Transport disposed");
        }
        const reconnectOperation = this.reconnectChain.then(async () => {
            if (this.disposed) {
                throw new Error("Transport disposed");
            }
            try {
                this.options?.onBeforeReconnect?.();
            }
            catch {
                // Ignore hook failures so reconnect can proceed.
            }
            this.lastHeartbeatPongAt = null;
            const previousSession = this.session;
            this.session = this.createSession();
            await this.closeSession(previousSession);
        });
        this.reconnectChain = reconnectOperation.catch(() => undefined);
        await reconnectOperation;
    }
    isHeartbeatFresh(maxAgeMs = 15_000) {
        return (this.lastHeartbeatPongAt !== null && performance.now() - this.lastHeartbeatPongAt <= maxAgeMs);
    }
    async dispose() {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        await this.closeSession(this.session);
    }
    closeSession(session) {
        this.intentionalCloseDepth += 1;
        return session.runtime.runPromise(Scope.close(session.clientScope, Exit.void)).finally(() => {
            this.intentionalCloseDepth = Math.max(0, this.intentionalCloseDepth - 1);
            session.runtime.dispose();
        });
    }
    createSession() {
        const protocolFactory = this.options?.createProtocolLayer ?? createWsRpcProtocolLayer;
        const sessionId = this.nextSessionId + 1;
        this.nextSessionId = sessionId;
        this.activeSessionId = sessionId;
        const lifecycleHandlers = this.lifecycleHandlers;
        const protocolLayer = protocolFactory(this.url, {
            ...lifecycleHandlers,
            isActive: () => !this.disposed &&
                this.activeSessionId === sessionId &&
                (lifecycleHandlers?.isActive?.() ?? true),
            isCloseIntentional: () => this.disposed ||
                this.intentionalCloseDepth > 0 ||
                lifecycleHandlers?.isCloseIntentional?.() === true,
            onHeartbeatPong: () => {
                this.lastHeartbeatPongAt = performance.now();
                lifecycleHandlers?.onHeartbeatPong?.();
            },
            onHeartbeatTimeout: () => {
                lifecycleHandlers?.onHeartbeatTimeout?.();
                if (this.disposed || this.activeSessionId !== sessionId) {
                    return;
                }
                // The protocol reopens the socket on its own after a ping timeout but
                // never re-sends in-flight stream requests; replacing the session
                // routes every subscription through its retry loop instead.
                void this.reconnect().catch(() => undefined);
            },
            onRequestStart: (info) => {
                lifecycleHandlers?.onRequestStart?.(info);
                if (!info.stream) {
                    return;
                }
                for (const listener of this.streamRequestStartListeners) {
                    listener({ tag: info.tag });
                }
            },
        });
        const rootLayer = this.options?.tracingLayer
            ? Layer.mergeAll(protocolLayer, this.options.tracingLayer)
            : protocolLayer;
        const runtime = ManagedRuntime.make(rootLayer);
        const clientScope = runtime.runSync(Scope.make());
        return {
            runtime,
            clientScope,
            clientPromise: runtime.runPromise(Scope.provide(clientScope)(makeWsRpcProtocolClient)),
        };
    }
    logWarning(message, metadata) {
        const logWarning = this.options?.logWarning;
        if (logWarning) {
            logWarning(message, metadata);
        }
        else {
            Effect.runSync(Effect.logWarning(message, metadata));
        }
    }
    runStreamOnSession(session, connect, listener, isActive, markValueReceived) {
        let resolveCompleted;
        let rejectCompleted;
        const completed = new Promise((resolve, reject) => {
            resolveCompleted = resolve;
            rejectCompleted = reject;
        });
        const cancel = session.runtime.runCallback(Effect.promise(() => session.clientPromise).pipe(Effect.flatMap((client) => Stream.runForEach(connect(client), (value) => Effect.sync(() => {
            if (!isActive()) {
                return;
            }
            markValueReceived();
            try {
                listener(value);
            }
            catch {
                // Ignore listener errors so the stream stays live.
            }
        })))), {
            onExit: (exit) => {
                if (Exit.isSuccess(exit)) {
                    resolveCompleted();
                    return;
                }
                rejectCompleted(Cause.squash(exit.cause));
            },
        });
        return {
            cancel,
            completed,
        };
    }
}
function sleep(ms) {
    return Effect.runPromise(Effect.sleep(Duration.millis(ms)));
}
